import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileVideo } from "lucide-react";
import api from "@/lib/api";

export function UploadFootage({ onUploaded }) {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const [phase, setPhase] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setPct(0); setPhase("Starting…");
    try {
      const init = await api.post("/media/upload/init", { filename: file.name, size: file.size, mime: file.type || "video/mp4" });
      const { upload_id, chunk_size } = init.data;
      let sent = 0;
      while (sent < file.size) {
        const chunk = file.slice(sent, Math.min(sent + chunk_size, file.size));
        await api.post(`/media/upload/${upload_id}/chunk`, chunk, { headers: { "Content-Type": "application/octet-stream" } });
        sent += chunk.size;
        setPct(Math.round((sent / file.size) * 100)); setPhase(`Uploading ${Math.round(sent / 1048576)} / ${Math.round(file.size / 1048576)} MB`);
      }
      setPhase("Validating & preparing…");
      const done = await api.post(`/media/upload/${upload_id}/complete`);
      toast.success(`${file.name} ready · ${done.data.duration?.toFixed(1)}s · ${done.data.width}×${done.data.height}`);
      onUploaded?.(done.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setPct(null); setPhase(""); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div data-testid="upload-footage" className="rounded-2xl border border-dashed border-white/15 p-4 flex flex-wrap items-center gap-3"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}>
      <input ref={ref} type="file" accept="video/*,.mp4,.mov,.webm,.mkv" className="hidden" data-testid="upload-input" onChange={(e) => upload(e.target.files?.[0])} />
      <FileVideo size={18} className="text-cyan-400" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold">Upload your own footage</div>
        <div className="text-[11px] text-slate-500">MP4 / MOV / WEBM up to 1 GB — then trim, add music, reframe, auto-caption and publish it like any Vectra render.</div>
        {pct !== null && <div className="mt-2"><div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 transition-[width]" style={{ width: `${pct}%` }} /></div><div data-testid="upload-progress" className="text-[10px] font-mono text-slate-400 mt-1">{phase} · {pct}%</div></div>}
      </div>
      <button data-testid="upload-button" disabled={pct !== null} onClick={() => ref.current?.click()} className="text-xs px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 disabled:opacity-40 inline-flex items-center gap-1">{pct !== null ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Choose video</button>
    </div>
  );
}
